import clsx from "clsx";
import { ReactNode, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toSlug } from "common";
import { useUser } from "../store";
import { lastPayerOf } from "../util/lastViewed";
import "./FacilityTabs.scss";

/**
 * Tabs above the residents table: one per facility the user can see, and under them the payers of the current facility.
 * `trailing` sits at the right end of the facility tabs; `search` at the right end of the payer pills.
 */
export default function FacilityTabs({ trailing, search }: { trailing?: ReactNode; search?: ReactNode }) {
  const { user, facility, payer } = useUser();
  const payersRef = useRef<HTMLUListElement>(null);
  // With many payers the pills scroll, so bring the current one into view
  useEffect(() => {
    payersRef.current?.querySelector('[aria-current="page"]')?.scrollIntoView?.({ inline: "center", block: "nearest" });
  }, [facility, payer]);
  const facilities: string[] = user?.facilities ?? [];
  const payersOf = (name: string): string[] => user?.facilitiesPayers?.[name] ?? [];
  const activeFacility = toSlug(facility ?? "");
  const activePayer = toSlug(payer ?? "");

  const currentFacility = facilities.find((name) => toSlug(name) === activeFacility);
  const payers = currentFacility ? payersOf(currentFacility) : [];
  if (facilities.length <= 1 && payers.length <= 1 && !trailing && !search) return null;
  const showTabs = facilities.length > 1 || payers.length > 1;
  const showPayers = showTabs && Boolean(currentFacility) && payers.length > 1;

  const pathFor = (facilityName: string, payerName: string) => `/${toSlug(user.group)}/${toSlug(facilityName)}/${toSlug(payerName)}`;
  // Opens the payer last viewed in that facility; failing that, the one you are on if it has it, else its first
  const payerAfterSwitching = (facilityName: string) => {
    const options = payersOf(facilityName);
    return lastPayerOf(user, facilityName) ?? options.find((name) => toSlug(name) === activePayer) ?? options[0];
  };

  return (
    <nav className="facility-tabs" aria-label="Switch facility and payer">
      <div className="facility-tabs-bar">
        {showTabs && (
          <ul className="facility-tabs-row is-facilities" aria-label="Facilities">
            {facilities.map((name) => {
              const target = payerAfterSwitching(name);
              const active = toSlug(name) === activeFacility;
              return (
                <li key={name}>
                  {target ? (
                    <Link to={pathFor(name, target)} className={clsx("facility-tab", { "is-active": active })} aria-current={active ? "page" : undefined}>
                      <i className="fa-solid fa-building" aria-hidden="true" />
                      <span className="capitalize">{name}</span>
                    </Link>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
        {trailing && <div className="facility-tabs-trailing">{trailing}</div>}
      </div>
      {(showPayers || search) && (
        <div className="facility-payers-bar">
          {showPayers && currentFacility && (
            <ul className="facility-tabs-row is-payers" aria-label={`Payers of ${currentFacility}`} ref={payersRef}>
              {payers.map((name) => {
                const active = toSlug(name) === activePayer;
                return (
                  <li key={name}>
                    <Link to={pathFor(currentFacility, name)} className={clsx("payer-tab", { "is-active": active })} aria-current={active ? "page" : undefined}>
                      <span className="uppercase">{name}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
          {search && <div className="facility-tabs-search">{search}</div>}
        </div>
      )}
    </nav>
  );
}
